import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'

import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { SummaryItem } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { placeholder } from '@utils/placeholder'

import { FilterQueryProps } from '@model/FilterFields'
import { WalletTypeEnum } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

interface IBRebateApprovalsSummary {
  summaryItems: SummaryItem[]
  forceRefresh: () => void
  isLoading: boolean
}

export const useIBRebateApprovalsSummary = (
  search?: FilterQueryProps
): IBRebateApprovalsSummary => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { account } = useAccountReadContext()
  const { compactNumber, formatNumber, formatMoney } = useFormatNumber()
  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const ibId = account?.clientIntroducingBroker?.id

  const { data: summaryData, isLoading } = useQuery({
    queryKey: queryKeys.ib.rebates.approvalsSummary(ibId, search as Record<string, unknown>),
    queryFn: () => apiClient.getRebateApprovalSummary(ibId!, { search }),
    enabled: !!ibId,
  })

  const { data: availableBalance, isLoading: isAvailableBalanceLoading } = useQuery({
    queryKey: queryKeys.wallet.list({ walletTypeId: WalletTypeEnum.IB } as any),
    queryFn: () => apiClient.getWallets({ walletTypeId: WalletTypeEnum.IB }),
    enabled: !!ibId,
  })

  const approvalsTotalCommission = summaryData?.approvalsTotalCommission ?? 0
  const balanceOnWallet = availableBalance?.[0]?.balance ?? 0

  const formattedValue = (value: number) => {
    if (isMobile) {
      return (
        <Tooltip content={`$${formatNumber(value)}`} disabled={!value || value < 1_000_000}>
          ${value ? compactNumber(value, 7) : placeholder.dash(value)}
        </Tooltip>
      )
    }

    return formatMoney(value, 'USD', value > 0 ? 2 : 0)
  }

  const summaryItems = useMemo<SummaryItem[]>(() => {
    return [
      {
        title: t('IB.RebatesPage.ApprovalsPage.Your Commission'),
        value: formattedValue(approvalsTotalCommission),
      },
      {
        title: t('IB.RebatesPage.ApprovalsPage.Rebates to Approve'),
        value: summaryData?.rebatesToApprove || 0,
      },
      {
        title: t('IB.RebatesPage.ApprovalsPage.Rebates Paid'),
        value: summaryData?.paidRebates || 0,
      },
      {
        title: t('Wallet.Available Balance'),
        value: formattedValue(balanceOnWallet),
      },
    ]
  }, [summaryData, t, isMobile, approvalsTotalCommission, balanceOnWallet])

  const forceRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.ib.rebates.approvalsSummary(ibId, search as Record<string, unknown>),
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.wallet.list({ walletTypeId: WalletTypeEnum.IB } as any),
    })
  }, [queryClient, ibId, search])

  return {
    summaryItems,
    forceRefresh,
    isLoading: isLoading || isAvailableBalanceLoading,
  }
}
